import { useEffect, useMemo } from "react"
import type { SubmitHandler } from "react-hook-form"
import { useFormContext } from "react-hook-form"
import { CircleAlert, LoaderCircle } from "lucide-react"
import { Sheet, SheetContent } from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import useUserEventSideSheet from "@/modules/calendar/stores/use-user-event-side-sheet"
import useEventModal from "@/modules/calendar/stores/use-event-modal"
import isLocked from "@/modules/calendar/utils/is-locked"
import isARecurringEvent from "@/modules/calendar/utils/is-a-recurring-event"
import SideSheetHeader from "./side-sheet-header"
import EventTitle from "./event-title"
import EventDay from "./event-day"
import EventSchedule from "./event-schedule"
import EventCategory from "./event-category"
import EventReplay from "./event-replay"
import EventFrequency from "./event-frequency"
import useCreateEvent from "@/modules/calendar/queries/use-create-event"
import useUpdateEvent from "@/modules/calendar/queries/use-update-event"
import { toast } from "sonner"
import { isClientError } from "@educabot/fetcher"
import sortRecurringDays from "@/modules/calendar/utils/sort-recurring-days"
import useCalendarConfig from "@/modules/calendar/stores/use-calendar-config"
import extractISODate from "@/modules/calendar/utils/extract-iso-date"
import type { UserEventForm } from "@/modules/calendar/types/user-event-form"

const getTextByType = (type: "addition" | "edition" | "details"): string => {
  switch (type) {
    case "addition":
      return "Crear evento"
    case "edition":
      return "Editar evento"
    case "details":
      return "Detalles evento"
    default:
      return ""
  }
}

export default function UserEventSideSheet() {
  const { isOpen, closeSideSheet, type, event, isAddition, isDetails, isEdition } =
    useUserEventSideSheet()

  const { openEventModal } = useEventModal()
  const { currentWeek } = useCalendarConfig()
  const { handleSubmit, reset, formState, watch } = useFormContext<UserEventForm>()

  const { isPending: createEventIsPending, mutateAsync: createEventAsyncMutation } =
    useCreateEvent()
  const { isPending: updateEventIsPending, mutateAsync: updateEventAsyncMutation } =
    useUpdateEvent()

  const isAnythingPending = createEventIsPending || updateEventIsPending

  const formHasChanges = useMemo(() => {
    if (!formState.defaultValues) return false

    const currentValues = watch()
    const defaultValues = formState.defaultValues

    return (
      JSON.stringify({
        title: defaultValues.title?.trim(),
        startDate: defaultValues.startDate,
        endDate: defaultValues.endDate,
        category: defaultValues.category,
        canShowReccurringDays: isARecurringEvent(event)
          ? defaultValues.canShowReccurringDays
          : false,
        recurringDays: sortRecurringDays(defaultValues.recurringDays),
      }) !==
      JSON.stringify({
        title: currentValues.title?.trim(),
        startDate: currentValues.startDate,
        endDate: currentValues.endDate,
        category: currentValues.category,
        canShowReccurringDays: isARecurringEvent(event)
          ? currentValues.canShowReccurringDays
          : false,
        recurringDays: sortRecurringDays(currentValues.recurringDays),
      })
    )
  }, [formState, watch(), event])

  const wantToSwitchFromRecurringToSingle =
    isARecurringEvent(event) &&
    (!watch("canShowReccurringDays") || watch("recurringDays").length === 0)

  const canDisabledSubmitButton =
    isAnythingPending || (isEdition && (!formHasChanges || wantToSwitchFromRecurringToSingle))

  useEffect(() => {
    if (event) {
      reset({
        title: event.parent.title,
        startDate: new Date(event.startDate).toISOString(),
        endDate: new Date(event.endDate).toISOString(),
        category: event.parent.category,
        recurringDays: event.parent.days,
        canShowReccurringDays: isARecurringEvent(event),
      })
    }
  }, [event, reset])

  const handleOnClose = () => {
    if (isAnythingPending) return
    reset()
    closeSideSheet()
  }

  const createEvent = async (data: Omit<UserEventForm, "canShowReccurringDays">) => {
    try {
      const payload = {
        body: data,
        params: {
          startDate: extractISODate(currentWeek.startDate),
          endDate: extractISODate(currentWeek.endDate),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      }

      await createEventAsyncMutation(payload)
      handleOnClose()

      const isRecurring = data.recurringDays.length > 0
      toast.success(isRecurring ? "Los eventos han sido creados" : "El evento ha sido creado")
    } catch (error) {
      toast.error("Algo salió mal", {
        description: isClientError(error) ? error.message : "No se pudo realizar la acción",
      })
    }
  }

  const editEvent = async (data: Omit<UserEventForm, "canShowReccurringDays">) => {
    try {
      const { canShowReccurringDays, recurringDays } = watch()

      const body = {
        ...data,
        isBulkUpdate: canShowReccurringDays && recurringDays.length > 0,
      }

      const payload = {
        eventId: event.id,
        body,
        params: {
          returnStart: extractISODate(currentWeek.startDate),
          returnEnd: extractISODate(currentWeek.endDate),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      }

      await updateEventAsyncMutation(payload)
      handleOnClose()

      const isRecurring = data.recurringDays.length > 0
      toast.success(isRecurring ? "Los eventos han sido editados" : "El evento ha sido editado")
    } catch (_) {
      return false
    }
  }

  const onSubmit: SubmitHandler<UserEventForm> = async (form) => {
    const { canShowReccurringDays, ...restOfForm } = form

    const data = {
      ...restOfForm,
      recurringDays: canShowReccurringDays ? form.recurringDays : [],
    }

    if (isAddition) {
      await createEvent(data)
    } else if (isARecurringEvent(event)) {
      openEventModal({ type: "edition", event })
    } else {
      await editEvent(data)
    }
  }

  return (
    <Sheet open={isOpen} onOpenChange={handleOnClose}>
      <SheetContent>
        <SideSheetHeader isPending={isAnythingPending} />
        <form
          className="flex size-full flex-col gap-3 overflow-hidden pt-9"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div
            className={`size-full max-h-full overflow-y-auto overflow-x-hidden ${isAnythingPending ? "pointer-events-none opacity-50" : ""}`}
          >
            <p className="text-sm font-medium text-gray-500">{getTextByType(type)}</p>
            <div className="flex w-full flex-col gap-5">
              <EventTitle />
              <EventDay />
              <EventSchedule />
              <EventCategory />
              <EventReplay />
              <EventFrequency />
            </div>

            {isLocked(event) && isDetails && (
              <div className="mt-8 w-full border-t border-solid border-t-gray-300 pt-8">
                <p className="flex select-none items-center justify-center gap-2 text-xs font-normal text-gray-600">
                  <CircleAlert className="!size-5 text-gray-500" />
                  <span>Este evento no se puede editar o eliminar</span>
                </p>
              </div>
            )}
          </div>

          {!isDetails && (
            <div className="flex h-auto w-full shrink-0 items-center justify-end overflow-hidden border-t border-solid border-t-gray-300 pt-4">
              <Button disabled={canDisabledSubmitButton} type="submit">
                {isAnythingPending && <LoaderCircle className="animate-spin" />}
                {isAddition ? "Crear evento" : "Guardar"}
              </Button>
            </div>
          )}
        </form>
      </SheetContent>
    </Sheet>
  )
}
